import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

/** App-wide constants for stashstr.news */
export const APP_TAG = 'stashstr.news';
export const APP_ALT = 'stashstr.news forum thread';

export const RELAYS = [
  'wss://relay.ditto.pub',
  'wss://relay.damus.io',
  'wss://relay.dreamith.to',
];

export const TERRITORIES = [
  'monero',
  'bitcoin',
  'nostr',
  'tech',
  'asksn',
  'memes',
  'library',
  'jobs',
  'bounties',
  'polls',
] as const;

export const POST_POW = 21;
export const COMMENT_POW = 20;
export const POLL_VOTE_POW = 18;
export const REACTION_POW = 18;

export const TITLE_MIN = 10;
export const TITLE_MAX = 120;
export const BODY_MAX = 2000;
export const COMMENT_MAX = 1000;
export const IMAGES_MAX = 4;
export const IMAGE_MAX_MB = 5;
export const POLL_MAX_OPTIONS = 6;

export const HIDE_UNPROVEN_KEY = 'stashstr-hide-unproven';
export const HIDE_UNPROVEN_DEFAULT = true;

/** Formats a unix timestamp (seconds) as a compact relative time, e.g. "3h", "2d". */
export function timeAgo(ts: number): string {
  const seconds = Math.floor(Date.now() / 1000) - ts;
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}

function getTag(e: NostrEvent, name: string): string | undefined {
  return e.tags.find(([t]) => t === name)?.[1];
}

/** Extracts the `title` tag of an event. */
export function getTitle(e: NostrEvent): string {
  return getTag(e, 'title') ?? '';
}

/** Extracts all `t` tag values (territories) in lowercase. */
export function getTerritories(e: NostrEvent): string[] {
  return e.tags
    .filter(([t]) => t === 't')
    .map(([, v]) => v?.toLowerCase())
    .filter((v): v is string => Boolean(v));
}

/** Extracts the first `r` tag value (canonical link URL). */
export function getLink(e: NostrEvent): string | undefined {
  const link = getTag(e, 'r');
  return sanitizeUrl(link);
}

/** Extracts the `poll` tag value (poll event id), if any. */
export function getPollId(e: NostrEvent): string | undefined {
  return getTag(e, 'poll');
}

/**
 * Extracts image URLs from NIP-94 `imeta` tags.
 * imeta format: ["imeta", "url https://... ", "m image/jpeg", ...]
 */
export function getImetas(e: NostrEvent): string[] {
  const urls: string[] = [];
  for (const tag of e.tags) {
    if (tag[0] !== 'imeta') continue;
    for (const part of tag.slice(1)) {
      if (part.startsWith('url ')) {
        const url = sanitizeUrl(part.slice(4).trim());
        if (url) urls.push(url);
      }
    }
  }
  return urls;
}

/** Returns the hostname of a URL, or undefined if invalid. */
export function extractDomain(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return undefined;
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

/**
 * Sanitizes a URL from untrusted event data.
 * Only allows https: and http: protocols. Returns undefined otherwise.
 * Prevents javascript:, data:, and other injection vectors.
 */
export function sanitizeUrl(u: string | undefined): string | undefined {
  if (!u) return undefined;
  const trimmed = u.trim();
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      return parsed.toString();
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/** Validates a stashstr post (kind 11). */
export function isValidStashItem(e: NostrEvent): boolean {
  if (e.kind !== 11) return false;
  const title = getTitle(e);
  if (title.length < TITLE_MIN || title.length > TITLE_MAX) return false;
  const territories = getTerritories(e);
  if (territories.length < 1 || territories.length > 2) return false;
  const hasBody = e.content.trim().length > 0;
  const hasLink = getLink(e) !== undefined;
  return hasBody || hasLink;
}

/** Hot ranking score, similar to Reddit's. */
export function hotScore(
  zaps: number,
  comments: number,
  reactions: number,
  createdAt: number,
): number {
  const ageHours = Math.max(0, (Date.now() / 1000 - createdAt) / 3600);
  const sats = zaps * 10 + comments * 3 + reactions * 2;
  return sats / Math.pow(ageHours + 2, 1.3);
}

/** Encodes an event as a nevent with relay hints. */
export function toNevent(e: NostrEvent): string {
  return nip19.neventEncode({
    id: e.id,
    relays: RELAYS,
    author: e.pubkey,
  });
}

/**
 * Encodes a pubkey as an npub (profile route).
 * Returns null when the pubkey is not valid 32-byte hex — callers must
 * render a non-link fallback instead of crashing.
 */
export function toNpub(pubkey: string): string | null {
  try {
    return nip19.npubEncode(pubkey);
  } catch {
    return null;
  }
}

/** Hook-less localStorage helper for the hide-unproven toggle. */
export function readHideUnproven(): boolean {
  if (typeof window === 'undefined') return HIDE_UNPROVEN_DEFAULT;
  const stored = window.localStorage.getItem(HIDE_UNPROVEN_KEY);
  if (stored === null) return HIDE_UNPROVEN_DEFAULT;
  return stored === 'true';
}

export function writeHideUnproven(value: boolean): void {
  window.localStorage.setItem(HIDE_UNPROVEN_KEY, value ? 'true' : 'false');
}

/** Linkifies plaintext content safely (no HTML injection). */
export function linkifyParts(text: string): Array<
  | { type: 'text'; value: string }
  | { type: 'url'; value: string; href: string }
> {
  const parts: Array<
    | { type: 'text'; value: string }
    | { type: 'url'; value: string; href: string }
  > = [];
  const regex = /https?:\/\/[^\s<>"')\]]+/g;
  let lastIndex = 0;
  for (const match of text.matchAll(regex)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, index) });
    }
    const href = sanitizeUrl(match[0]);
    if (href) {
      parts.push({ type: 'url', value: match[0], href });
    } else {
      parts.push({ type: 'text', value: match[0] });
    }
    lastIndex = index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return parts;
}
